import shortUUID from "short-uuid";
import type {
  Data,
  DataSource,
  IDataMap,
  IInstagramDataSourceV2,
  IInstagramDataV2,
  ILocalData,
  PartialData,
  PartialInsagramDataV2,
  PartialLocalData,
} from "../types/data-source";
import { isObject } from "./value";

type GetDataSourceValueArgs = {
  dataMap: IDataMap;
  keyString: string;
};

export const getDataSourceValue = ({
  dataMap,
  keyString,
}: GetDataSourceValueArgs) => {
  try {
    const [dataSourceName, ...keys] = keyString.split(".");

    if (!dataSourceName) {
      throw new Error("incorrect template syntax");
    }

    const dataSource = dataMap[dataSourceName]?.data;

    if (!dataSource) {
      throw new Error("invalid data source name");
    }

    return getNestedObjectValue(dataSource, keys);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
};

export const isKeyOfObject = (obj: object, key: string) =>
  Object.keys(obj).includes(key);

export const getNestedObjectValue = (object: unknown, keys: string[]) => {
  let nestedValue: unknown = object;

  for (const key of keys) {
    if (!isObject(nestedValue) || !isKeyOfObject(nestedValue, key)) {
      throw new Error("invalid key in object");
    }

    nestedValue = nestedValue[key as keyof typeof nestedValue];
  }

  return nestedValue;
};

export const createData = (initialData?: PartialData) => {
  switch (initialData?.name) {
    case "instagram":
      return createInstagramDataV2(initialData);
    case "local":
      return createLocalData(initialData);
    default:
      return createLocalData();
  }
};

export const createLocalData = (initialData?: PartialLocalData): ILocalData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "local",
    data: initialData?.data ?? {},
  };
};

export const createInstagramDataV2 = (
  initialData?: PartialInsagramDataV2
): IInstagramDataV2 => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "instagram",
    data: initialData?.data ?? { username: "", posts: [] },
  };
};

export const createDataMap = (initialDataMap?: {
  [name: string]: PartialData;
}): IDataMap => {
  return {
    local: createLocalData(),
    ...Object.entries(initialDataMap ?? {}).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: createData(value),
      }),
      {}
    ),
  };
};

export const getData = <T extends Data = ILocalData>(
  dataMap: IDataMap,
  key: string
) => {
  const dataSource = dataMap[key] as T | undefined;
  return dataSource;
};

export const getDataSourceBySourceType = <T extends DataSource>(
  dataSources: DataSource[],
  sourceType: T["sourceType"]
): T | undefined => {
  const dataSourceFromSourceType = dataSources.find(
    (dataSource): dataSource is T => dataSource.sourceType === sourceType
  );

  return dataSourceFromSourceType;
};

export const getInstagramSourceType = (dataSource: IInstagramDataSourceV2) => {
  return dataSource.sourceParam.source.type;
};
